import { get, httpDelete, post, put } from 'api/client';
import { getExtendedTimeout } from 'utils/request';
import { createSearchFromParams } from 'utils/url';
import { MediaApi } from 'api/media';
import { imageToMediaLibraryItem } from 'utils/mediaLibrary';
import { BrandHubConfig } from 'app/config/brandHub';
import {
  getAiHubBaseURL,
  getPublicAiHubBaseURL,
} from 'app/config/aiHubEndpointMode';
import {
  AiModelsResponse,
  BrandInitializationRequest,
  BrandInitializationResponse,
  BrandProfile,
  BrandProfileAnalytics,
  BrandProfileInsights,
  ContentIdeasSuggestionsResponse,
  Creative,
  ExecutionStatusResponse,
  ImageResult,
  PageConnection,
  PostIdeasRequest,
  PostIdeasResponse,
  WebCrawlResult,
} from 'app/modules/brandHub/types';

function normalizeImageSuggestions(
  raw: unknown,
  fallbackSlots?: number[],
): ImageResult[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, i): ImageResult => {
    const slotIndex = fallbackSlots?.[i] ?? i;
    if (typeof item === 'string') {
      return { slotIndex, status: 'ok', image: item, error: null };
    }
    if (item && typeof item === 'object') {
      const r = item as Partial<ImageResult>;
      return {
        slotIndex: r.slotIndex ?? slotIndex,
        status: r.status ?? (r.image ? 'ok' : 'failed'),
        image: r.image ?? null,
        error: r.error ?? null,
      };
    }
    return { slotIndex, status: 'failed', image: null, error: 'invalid item' };
  });
}

export type SocialProfileWithPageId = {
  pageId: number;
  platform: string;
  externalId: string;
  username: string;
};

export type BatchSyncHistoryRequest = {
  userAccountPublicId: string;
  postLimit?: number;
  profiles: SocialProfileWithPageId[];
};

export type BatchSyncResult = {
  syncId: string;
  platform: string;
  username: string;
  socialAccountId: string;
};

export type BatchSyncHistoryResponse = {
  batchId: string;
  status: 'started';
  syncs: BatchSyncResult[];
};

export type SyncStatusType = 'in_progress' | 'completed' | 'failed';

export type BatchStatusType =
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'partial'
  | 'not_found';

export type SyncStatusItem = {
  syncId: string;
  platform: string;
  username: string;
  status: SyncStatusType;
  postsImported: number | null;
  errorMessage: string | null;
};

export type BatchSyncStatusResponse = {
  batchId: string;
  status: BatchStatusType;
  syncs: SyncStatusItem[];
  totalSyncs: number;
  completedSyncs: number;
  failedSyncs: number;
  inProgressSyncs: number;
};

export const BrandHubApi = {
  createBrandProfile(
    data: Omit<BrandProfile, 'id' | 'createdAt' | 'updatedAt' | 'logoSrc'>,
  ): Promise<BrandProfile> {
    return post('/brand-profiles', data, {
      baseURL: getAiHubBaseURL(),
    });
  },
  updateBrandProfile(
    id: number | string,
    data: Omit<BrandProfile, 'id' | 'createdAt' | 'updatedAt' | 'logoSrc'>,
  ): Promise<BrandProfile> {
    return put(`/brand-profiles/${id}`, data, {
      baseURL: getAiHubBaseURL(),
    });
  },
  async saveBrandProfile({
    logoSrc,
    ...data
  }: Omit<BrandProfile, 'id' | 'createdAt' | 'updatedAt' | 'logoSrc'> & {
    id?: string;
    logoSrc: string | File | null;
  }): Promise<BrandProfile> {
    const newLogoSrc = await (async () => {
      if (logoSrc instanceof File) {
        const response = await MediaApi.uploadImage({
          file: logoSrc,
        });

        return imageToMediaLibraryItem(Object.values(response)[0])?.data
          .thumbnail.src;
      }

      return logoSrc;
    })();

    const completeData = {
      ...data,
      logoSrc: newLogoSrc,
    };

    if (completeData.id) {
      return BrandHubApi.updateBrandProfile(
        completeData.id.toString(),
        completeData,
      );
    }
    return BrandHubApi.createBrandProfile(completeData);
  },
  deleteBrandProfile(id: string | number): Promise<void> {
    return httpDelete(`/brand-profiles/${id}`, {
      baseURL: getAiHubBaseURL(),
    });
  },
  getOneBrandProfile(id: string | number): Promise<BrandProfile> {
    return get(`/brand-profiles/${id}`, {
      baseURL: getAiHubBaseURL(),
    });
  },
  getOneBrandProfileAnalytics(
    id: string | number,
  ): Promise<BrandProfileAnalytics> {
    return get(`/brand-profiles/${id}/analytics`, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(),
    });
  },
  getOneBrandProfileInsights(
    id: string | number,
  ): Promise<BrandProfileInsights> {
    return get(`/brand-profiles/${id}/insights`, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  getAllBrandProfiles(): Promise<BrandProfile[]> {
    return get('/brand-profiles', {
      baseURL: getAiHubBaseURL(),
    });
  },
  suggestBrandProfile(data: {
    postsText?: string;
    bodyText?: string;
    planType: string;
    logo?: string;
    analyticsProfiles?: { profileId: string; pageType: number }[];
  }): Promise<BrandProfile> {
    return post('/suggest-brand-profiles', data, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  getContentIdeasSuggestions(data: {
    brandProfileId: string;
    planType: string;
    text?: string;
  }): Promise<ContentIdeasSuggestionsResponse> {
    return post(`/brand-profiles/content-ideas`, data, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  creativesOptions(): Promise<{
    creativeLength: {
      value: string;
      label: string;
    }[];
    toneOfVoice: {
      value: string;
      label: string;
    }[];
    counts: {
      value: number;
      label: string;
    }[];
    language: {
      value: string;
      label: string;
      bcp47: string;
    }[];
    postType: {
      value: string;
      label: string;
    }[];
  }> {
    return get('/creatives/options', {
      baseURL: getAiHubBaseURL(),
    });
  },
  publicCreativesOptions(): Promise<{
    creativeLength: { value: string; label: string }[];
    toneOfVoice: { value: string; label: string }[];
    counts: { value: number; label: string }[];
    language: { value: string; label: string; bcp47: string }[];
    postType: { value: string; label: string }[];
  }> {
    return get('/free-tools/options', {
      baseURL: getPublicAiHubBaseURL(),
    });
  },
  getAiModels(type?: 'chat' | 'image' | 'agent'): Promise<AiModelsResponse> {
    const params = type ? `?type=${type}` : '';
    return get(`/ai/models${params}`, {
      baseURL: getAiHubBaseURL(),
    });
  },
  freeToolsGenerateCaptions(data: {
    postText: string;
    postType: string;
    writingStyle: string;
    captionLength: string;
    variationsCount: number;
    language?: string | null;
    useEmojis: boolean;
    useHashtags: boolean;
    pageType?: number;
  }): Promise<{ captions: Array<{ text: string }> }> {
    return post('/free-tools/captions', data, {
      baseURL: getPublicAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  freeToolsGenerateHashtags(data: {
    postText: string;
    postType?: string;
    hashtagStyle: string;
    hashtagCount?: number;
    variationsCount?: number;
    language?: string | null;
    pageType?: number;
  }): Promise<{ hashtags: Array<{ text: string }> }> {
    return post('/free-tools/hashtags', data, {
      baseURL: getPublicAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  freeToolsGenerateUsernames(data: {
    nameOrBrand: string;
    keywords?: string | null;
    accountType: string;
    category?: string;
    toneStyle?: string;
    usernameLength: string;
    variationsCount?: number;
    language?: string | null;
    pageType?: number;
  }): Promise<{ usernames: Array<{ text: string }> }> {
    return post('/free-tools/usernames', data, {
      baseURL: getPublicAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  freeToolsGenerateBios(data: {
    nameOrBrand: string;
    keywords?: string | null;
    accountType: string;
    category?: string;
    toneStyle?: string;
    bioType?: string;
    bioLength?: string;
    variationsCount?: number;
    includeEmojis?: boolean;
    language?: string | null;
    pageType?: number;
  }): Promise<{ bios: Array<{ text: string }> }> {
    return post('/free-tools/bios', data, {
      baseURL: getPublicAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  freeToolsGenerateComments(data: {
    originalComment: string;
    replyContext?: string | null;
    replyGoal: string;
    tone: string;
    replyLength: string;
    includeEmojis: boolean;
    includeLink?: string | null;
    variationsCount?: number;
    language?: string | null;
    pageType?: number;
  }): Promise<{ comments: Array<{ text: string }> }> {
    return post('/free-tools/comments', data, {
      baseURL: getPublicAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  createCreative({
    webCrawl,
    ...data
  }: {
    brandProfileId?: string;
    goal: string;
    pageType?: number;
    planType: string;
    creativeLength?: string;
    language?: string | null;
    toneOfVoice?: string;
    webCrawl?: WebCrawlResult | null;
  }): Promise<Creative> {
    return post(
      '/creatives',
      {
        ...data,
        referenceText: webCrawl?.articleText
          ? `URL: ${webCrawl?.url}, Text: ${webCrawl?.articleText}`
          : undefined,
      },
      {
        baseURL: getAiHubBaseURL(),
        timeout: getExtendedTimeout(5),
      },
    );
  },
  deleteCreative(id: string | number): Promise<void> {
    return httpDelete(`/creatives/${id}`, {
      baseURL: getAiHubBaseURL(),
    });
  },
  updateCreative({
    id,
    ...data
  }: Partial<Creative> & Pick<Creative, 'id'>): Promise<Creative> {
    return put(`/creatives/${id}`, data, {
      baseURL: getAiHubBaseURL(),
    });
  },
  getAllCreatives(data: { brandProfileId: string; page?: number }): Promise<
    ApiResponse<
      Creative[],
      {
        currentPage: number;
        firstPage: number;
        lastPage: number;
        perPage: number;
      }
    >
  > {
    return get(
      `/creatives${createSearchFromParams({
        brandProfileId: data.brandProfileId,
        page: data.page,
        perPage: BrandHubConfig.CREATIVES_PAGE_SIZE,
      })}`,
      {
        baseURL: getAiHubBaseURL(),
      },
    );
  },
  fetchWebCrawl(data: { url: string }): Promise<WebCrawlResult> {
    return post(`/web-crawls`, data, {
      baseURL: getAiHubBaseURL(),
    });
  },
  getAnalyticsProfiles({
    first = -1,
  }: {
    first?: number;
  } = {}): Promise<PageConnection> {
    return get(
      `/analytics/profiles${createSearchFromParams({
        first,
      })}`,
      {
        baseURL: getAiHubBaseURL(),
      },
    );
  },
  async generateImage(params: {
    style: string;
    prompt: string;
    count: number;
    aspectRatio: string;
    planType: string;
    type: string;
    referenceImages?: string[];
    model?: string;
  }): Promise<ImageResult[]> {
    const raw = await post<unknown>('/image-suggestions', params, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(10),
    });
    return normalizeImageSuggestions(raw);
  },
  async retryImage(params: {
    style: string;
    prompt: string;
    aspectRatio: string;
    planType: string;
    type: string;
    slotIndexes: number[];
    referenceImages?: string[];
    model?: string;
    idempotencyKey?: string;
  }): Promise<ImageResult[]> {
    const { idempotencyKey, ...body } = params;
    const raw = await post<unknown>('/image-suggestions/retry', body, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(10),
      headers: idempotencyKey
        ? { 'Idempotency-Key': idempotencyKey }
        : undefined,
    });
    return normalizeImageSuggestions(raw, params.slotIndexes);
  },
  postIdeas(params: PostIdeasRequest): Promise<PostIdeasResponse> {
    return post('/post-ideas', params, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  syncHistoryBatch(
    data: BatchSyncHistoryRequest,
  ): Promise<BatchSyncHistoryResponse> {
    return post('/social-posts/sync-history', data, {
      baseURL: getAiHubBaseURL(),
    });
  },
  getBatchSyncStatus(batchId: string): Promise<BatchSyncStatusResponse> {
    return get(`/social-posts/sync-history/batch/${batchId}/status`, {
      baseURL: getAiHubBaseURL(),
    });
  },
  brandInitialization(
    data: BrandInitializationRequest,
  ): Promise<BrandInitializationResponse> {
    return post('/brand-initialization', data, {
      baseURL: getAiHubBaseURL(),
      timeout: getExtendedTimeout(5),
    });
  },
  getExecutionStatus(entityId: string): Promise<ExecutionStatusResponse> {
    return get(`/execution-status/${entityId}`, {
      baseURL: getAiHubBaseURL(),
    });
  },
};
